import toast from "react-hot-toast";
import "./profileForm.scss";
import { useUpdateProfileMutation } from "../../../features/api/userApiSlice";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

// ✅ Validation Schema
const ProfileSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required."),
  lastName: Yup.string().required("Last name is required."),
  email: Yup.string()
    .email("Invalid email format.")
    .required("Email is required."),
  phoneNumber: Yup.string()
    .matches(
      /^(\d{3}-\d{3}-\d{4})$/,
      "Please enter a valid 10-digit phone number with dashes"
    )
    .nullable(),
});

// ✅ Phone formatter
// ✅ Auto-format phone number as XXX-XXX-XXXX
// const formatPhoneNumber = (value) => {
//   const digits = value.replace(/\D/g, ""); // remove non-digits
//   if (!digits) return "";
//   if (digits.length <= 3) return digits;
//   if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
//   return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
// }; // NO DASHES

const formatPhoneNumberWithDashes = (value) => {
  if (!value) return "";
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, "").slice(0, 10); // max 10 digits
  const len = digits.length;

  if (len < 4) return digits;
  if (len < 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const ProfileForm = ({ user }) => {
  const [updateUser, { isLoading }] = useUpdateProfileMutation();

  return (
    <Formik
      initialValues={{
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        email: user?.email || "",
        phoneNumber: user?.phoneNumber || "",
      }}
      validationSchema={ProfileSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          // remove dashes before sending to backend
          const formattedValues = {
            ...values,
            phoneNumber: values.phoneNumber,
            // phoneNumber: values.phoneNumber.replace(/-/g, ""), // This removes -
          };
          await updateUser(formattedValues).unwrap();
          toast.success("Profile updated successfully!");
        } catch (err) {
          toast.error(err?.data?.message || "Failed to update profile");
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, errors, touched, isSubmitting }) => (
        <Form className="profile-form">
          {/* First Name */}
          <div className="form-controls">
            <label htmlFor="firstName">
              First Name <span className="required-inputs">*</span>
            </label>
            <Field
              type="text"
              id="firstName"
              name="firstName"
              className={
                errors.firstName && touched.firstName ? "input-error" : ""
              }
            />
            <ErrorMessage name="firstName" component="span" className="error" />
          </div>

          {/* Last Name */}
          <div className="form-controls">
            <label htmlFor="lastName">
              Last Name <span className="required-inputs">*</span>
            </label>
            <Field
              type="text"
              id="lastName"
              name="lastName"
              className={
                errors.lastName && touched.lastName ? "input-error" : ""
              }
            />
            <ErrorMessage name="lastName" component="span" className="error" />
          </div>

          {/* Email */}
          <div className="form-controls">
            <label htmlFor="email">
              Email <span className="required-inputs">*</span>
            </label>
            <Field
              type="email"
              id="email"
              name="email"
              className={errors.email && touched.email ? "input-error" : ""}
            />
            <ErrorMessage name="email" component="span" className="error" />
          </div>

          {/* Phone */}
          <div className="form-controls">
            <label htmlFor="phoneNumber">Phone Number</label>
            <Field
              type="text"
              id="phoneNumber"
              name="phoneNumber"
              value={values.phoneNumber}
              onChange={(e) =>
                setFieldValue(
                  "phoneNumber",
                  formatPhoneNumberWithDashes(e.target.value)
                )
              }
              maxLength={12} // XXX-XXX-XXXX
            />
            <ErrorMessage
              name="phoneNumber"
              component="span"
              className="error"
            />
          </div>

          <button
            type="submit"
            className="save-button"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting || isLoading ? "Saving..." : "Save Profile"}
          </button>
        </Form>
      )}
    </Formik>
  );
};

export default ProfileForm;
